/** Returns "owner/repo" for any common GitHub repo reference, or null when it isn't one. */
export function parseGithubRepo(input: string): string | null {
    const value = input.trim();
    if (!value) return null;
    const ssh = value.match(/^git@github\.com:([\w.-]+)\/([\w.-]+?)(?:\.git)?\/?$/i);
    if (ssh) return `${ssh[1]}/${ssh[2]}`;
    const url = value.match(/^(?:https?:\/\/)?(?:www\.)?github\.com\/([\w.-]+)\/([\w.-]+?)(?:\.git)?(?:[/?#].*)?$/i);
    if (url) return `${url[1]}/${url[2]}`;
    // Bare "owner/repo"; a dotted first segment is a domain like lovable.app/foo, not an owner
    const short = value.match(/^([\w-]+)\/([\w.-]+)$/);
    if (short) return `${short[1]}/${short[2]}`;
    return null;
}

/** The import endpoint needs a branch; prefer the conventional defaults when the repo has them. */
export function pickDefaultBranch(branches: string[]): string {
    return ['main', 'master'].find(b => branches.includes(b)) ?? branches[0] ?? 'main';
}

/** Branch options and preselection for a repo; a known default branch is never swapped for another. */
export function resolveBranchSelection(
    branches: string[],
    knownDefault?: string | null,
): { branches: string[]; selected: string } {
    if (knownDefault) {
        const options = branches.includes(knownDefault) ? branches : [knownDefault, ...branches];
        return { branches: options, selected: knownDefault };
    }
    return { branches, selected: pickDefaultBranch(branches) };
}

export type ImportSource = 'lovable' | 'replit' | 'bolt' | 'github';

export interface BuilderHints {
    name: string;
    description?: string | null;
    homepage?: string | null;
    topics?: string[] | null;
}

/** Which builder a repo came from, read from what each builder writes when it creates the repo (description/homepage link, name prefix or topic). */
export function detectBuilder(repo: BuilderHints): ImportSource | null {
    const text = `${repo.name} ${repo.description ?? ''} ${repo.homepage ?? ''}`.toLowerCase();
    const topics = (repo.topics ?? []).map(t => t.toLowerCase());
    const tagged = (builder: string) => topics.some(t => t === builder || t.startsWith(`${builder}-`));
    if (/replit\.(com|app|dev)|^replit-/.test(text) || tagged('replit')) return 'replit';
    if (/lovable\.(dev|app)|gptengineer/.test(text) || tagged('lovable')) return 'lovable';
    if (/bolt\.(new|host)|stackblitz/.test(text) || tagged('bolt')) return 'bolt';
    return null;
}
